if (!customElements.get("variant-picker")) {
   class VariantPicker extends HTMLElement {
      constructor() {
         super();
         if (Shopify.designMode) {
            setTimeout(() => this.init(), 200);
         } else {
            this.init();
         }
      }

      init() {
         this.selectors = {
            error: ".m-product-form-message",
            variantIdInput: '[name="id"]',
            pickerFields: ["[data-picker-field]"],
            optionNodes: [".m-product-option--node"],
            productCustomLabel: "[data-custom-label]",
            productSubtitle: "[data-product-subtitle]",
            productBanner: "[data-variant-banner]",
            productSku: "[data-variant-sku]",
            productBarcode: "[data-variant-barcode]",
            productAvailability: "[data-availability]",
            productInventoryStatus: "[data-variant-inventory-status]",
            shareUrlInput: "[data-share-url]",
            stockCountdown: ".prod__stock-countdown",
            productWrapper: ".m-main-product--wrapper",
         };

         this.productWrapper = this.closest(this.selectors.productWrapper);
         this.productForm = this.productWrapper.querySelector("product-form");
         this.domNodes = queryDomNodes(this.selectors, this.productWrapper);
         this.ctaBtnText = window.MinimogStrings.addToCart;

         const themeProducts = window._themeProducts || {};
         const {
            productId,
            sectionId,
            productUrl,
            hasOnlyDefaultVariant,
            showFeaturedMedia,
         } = this.dataset;

         Object.assign(this, {
            productId,
            sectionId,
            productUrl,
            hasOnlyDefaultVariant: hasOnlyDefaultVariant === "true",
            showFeaturedMedia: showFeaturedMedia === "true",
            variantData: this.getVariantData(),
            variantMetafieldData: this.getVariantMetafieldData(),
            productData: Object.assign(
               this.getProductJson(),
               themeProducts[productId]
            ),
         });

         this.handleQueryString();
         this.handleVariantGroupImages();
         this.selectInitialVariant();

         this.setupEventListeners();
         this.initOptionSwatches();
      }

      handleQueryString() {
         const hasQueryString = window.location.search.includes("?variant=");
         const disableSelectedVariantDefault =
            this.dataset.disableSelectedVariantDefault === "true";
         const hasMultipleVariants =
            this.productData &&
            this.productData.variants &&
            this.productData.variants.length > 1;

         this.selectedVariantDefault =
            disableSelectedVariantDefault &&
            hasMultipleVariants &&
            !hasQueryString;

         if (this.selectedVariantDefault) {
            this.disableSelectedVariantDefault();
         }
      }

      handleVariantGroupImages() {
         if (this.dataset.enableVariantGroupImages === "true") {
            this.enableVariantGroupImages = true;
            this.variantGroupImages = this.getVariantGroupImageData();
         } else {
            this.enableVariantGroupImages = false;
         }
      }

      selectInitialVariant() {
         const variantIdInput = this.productWrapper.querySelector(
            this.selectors.variantIdInput
         );
         const selectedVariantId = Number(variantIdInput.value);
         let currentVariant = this.productData.variants.find(
            (variant) => variant.id === selectedVariantId
         );

         // If the selected/current variant is hidden, fall back to the first visible variant
         if (currentVariant && !this.isVariantVisible(currentVariant.id)) {
            const firstVisible = this.getVisibleVariants()[0];
            if (firstVisible) {
               currentVariant = firstVisible;
               variantIdInput.value = String(firstVisible.id);
            }
         }
         if (!currentVariant) {
            const firstVisible = this.getVisibleVariants()[0];
            if (firstVisible) {
               currentVariant = firstVisible;
               variantIdInput.value = String(firstVisible.id);
            }
         }

         let updateButtonText = "";
         if (currentVariant && currentVariant.inventory_quantity <= 0) {
            updateButtonText = "Sold Out";
         } else {
            updateButtonText = "Add to Cart";
         }

         const variantMetafield = this.variantMetafieldData.find(
            (metafield) => metafield.id === `${currentVariant.id}`
         );
         const status = variantMetafield.metafields.inventory_status
         if (status.toLowerCase().includes("preorder")) {
            updateButtonText = "Preorder"
         }
         if (status.toLowerCase().includes("coming soon")) {
            updateButtonText = "Coming soon"
         }
         if (status.toLowerCase().includes("backorder")) {
            updateButtonText = "Backorder"
         }

         this.selectedVariant = variantIdInput;
         this.currentVariant = currentVariant;
         this.productData.current_variant_id = currentVariant
            ? currentVariant.id
            : null;
         this.productData.initialVariant = currentVariant;

         if (currentVariant) {
            const isDiabled = updateButtonText === "Coming soon" ? true : !this.currentVariant.available;
            this.getDataImageVariant(currentVariant.id);
            this.hideSoldOutAndUnavailableOptions();
            this.updateButton(isDiabled, updateButtonText);
         }
      }

      setupEventListeners() {
         this.hasSetupMediaData = false;
         const mediaGalleryHandler = this.getMediaGallery.bind(this);

         mediaGalleryHandler();
         ["matchMobile", "unmatchMobile"].forEach((event) =>
            document.addEventListener(event, mediaGalleryHandler)
         );

         this.addEventListener("change", this.onVariantChange);
      }

      getMediaGallery() {
         return this.waitForMediaGallery().then(
            this.setupMediaGallery.bind(this)
         );
      }

      waitForMediaGallery() {
         return new Promise((resolve) => {
            const checkMediaGallery = () => {
               this.mediaGallery =
                  this.productWrapper.querySelector("media-gallery");
               this.mediaGalleryMobile = this.productWrapper.querySelector(
                  "media-gallery-mobile"
               );

               const isMediaGalleryReady =
                  this.mediaGallery &&
                  (this.mediaGallery.view === "featured-product" ||
                     this.mediaGallery.view === "quick-view") &&
                  this.mediaGallery.mediaMode;

               const isBothGalleriesReady =
                  this.mediaGallery &&
                  this.mediaGalleryMobile &&
                  this.mediaGallery.mediaMode &&
                  this.mediaGalleryMobile.mediaMode;

               if (isMediaGalleryReady || isBothGalleriesReady) {
                  resolve();
               } else {
                  setTimeout(checkMediaGallery, 100);
               }
            };

            checkMediaGallery();
         });
      }

      setupMediaGallery() {
         const isMobile = MinimogTheme.config.mqlMobile;
         const effectiveMediaGallery = isMobile
            ? this.mediaGalleryMobile || this.mediaGallery
            : this.mediaGallery;
         this.media = effectiveMediaGallery;

         if (!this.hasSetupMediaData) {
            if (isMobile || effectiveMediaGallery.mediaMode === "slider") {
               this.slides =
                  effectiveMediaGallery.slider &&
                  effectiveMediaGallery.slider.slides;
               this.slidesNav =
                  effectiveMediaGallery.navSlider &&
                  effectiveMediaGallery.navSlider.slides;
            } else {
               this.mediaItems = effectiveMediaGallery.querySelectorAll(
                  ".m-product-media--item"
               );
            }

            this.layout = !isMobile ? effectiveMediaGallery.layout : undefined;
         }

         if (
            !this.showFeaturedMedia ||
            (this.enableVariantGroupImages && this.variantGroupImages.enable)
         ) {
            this.updateMedia();
         }
         this.hasSetupMediaData = true;
      }

      disableSelectedVariantDefault() {
         // Reset radio options
         this.querySelectorAll('input[type="radio"]').forEach((radioOption) => {
            radioOption.checked = false;
         });

         // Clear data-selected-value for radio picker fields
         this.querySelectorAll("[data-picker-field='radio']").forEach(
            (radio) => {
               radio.setAttribute("data-selected-value", "");
            }
         );

         // Reset select elements and clear data-selected-value for select picker fields
         this.querySelectorAll("[data-picker-field='select']").forEach(
            (pickerSelect) => {
               const selectElm = pickerSelect.querySelector("select");
               if (selectElm) selectElm.value = "";
               pickerSelect.setAttribute("data-selected-value", "");
            }
         );

         // Clear text content for selected option labels
         this.querySelectorAll(".option-label--selected").forEach((label) => {
            label.textContent = "";
         });

         // Reset variant ID if applicable
         if (this.productForm) {
            const variantIdEl = this.productForm.querySelector("[name='id']");
            if (variantIdEl && !this.hasOnlyDefaultVariant) {
               variantIdEl.value = "";
            }
         }
      }

      setProductAsUnavailable() {
         this.updateButton(true); // Disable button, clear text, mark as unavailable
         this.setUnavailable();
      }

      updateProductDetailsForVariant() {
         // Fetch additional data and update UI components to reflect the selected variant
         this.getDataImageVariant(this.currentVariant.id);
         this.updateMedia();
         this.updateBrowserHistory();
         this.updateVariantInput();
         this.updateProductMeta();
         this.updatePrice();

         // Update the button based on availability of the current variant
         const isDiabled = this.ctaBtnText === "Coming soon" ? true : !this.currentVariant.available;
         // const buttonText = isSoldOut ? window.MinimogStrings.soldOut : "";
         this.updateButton(isDiabled, this.ctaBtnText);

         // Hide options that are sold out or unavailable
         this.hideSoldOutAndUnavailableOptions();
      }

      onVariantChange() {
         // Retrieve selected options and variant
         this.getSelectedOptions();
         this.getSelectedVariant();

         // Always perform these updates on variant change
         this.updatePickupAvailability();
         this.removeErrorMessage();

         // If there's no current variant, set the product as unavailable and return early
         if (!this.currentVariant) {
            this.setProductAsUnavailable();
            return;
         }

         // Update the product details for the selected variant
         this.updateProductDetailsForVariant();
         this.updateVariantPickerVisibility();
         this.sqUpdateVariantPickerOptions();

         // Emit a custom event signaling a variant change
         const variantChangeEvent = new CustomEvent("variant:changed", {
            detail: { variant: this.currentVariant },
         });
         window.MinimogEvents.emit(
            `${this.productId}__VARIANT_CHANGE`,
            this.currentVariant,
            this
         );
         document.dispatchEvent(variantChangeEvent);
      }

      getDataImageVariant(variantId) {
         if (!this.variantGroupImages || !this.variantGroupImages.enable)
            return;

         const currentVariantMedia = this.variantGroupImages.mapping.find(
            (variant) => Number(variant.id) === variantId
         );
         if (currentVariantMedia) {
            this.currentVariantMedia = currentVariantMedia.media;
         }
      }

      getProductJson() {
         try {
            const productDataElement = this.productWrapper.querySelector(
               "#productData[type='application/json']"
            );
            if (!productDataElement)
               throw new Error("Fallback product data element not found.");
            return JSON.parse(productDataElement.textContent);
         } catch (error) {
            console.error("Error loading fallback product data:", error);
            return null; // Return null as a default value
         }
      }
      sqGetVariantFromOptionArray(product, options, metafieldData) {
         _validateProductStructure(product);
         _validateOptionsArray(options);
         var result = product.variants.filter(function (variant) {
            const variantMetafield = metafieldData.find(
               (metafield) => metafield.id === `${variant.id}`
            );
            if (variantMetafield.metafields.variant_visibility == false) {
               return false;
            }
            return options.every(function (option, index) {
               return variant.options[index] === option;
            });
         });
         return result[0] || null;
      }

      getSelectedVariant() {
         let variant;
         let options = [...this.options];

         // Attempt to find a variant matching the current options, reducing options from the end if necessary
         for (let attempt = 0; attempt < this.options.length; attempt++) {
            variant = this.sqGetVariantFromOptionArray(
               this.productData,
               options,
               this.variantMetafieldData
            );
            // If a variant is found, or no options are left, break out of the loop
            if (variant || options.length === 0) {
               break;
            }

            // Remove the last option and try again
            options.pop();
         }

         // If a variant is found, update the selected options and the current variant
         if (variant) {
            this.options = [...variant.options]; // Update options to match found variant
            this.updateSelectedOptions();
         }

         // Update the current variant, regardless of whether a match was found
         this.currentVariant = variant;
      }

      getSelectedOptions() {
         // Convert NodeList to Array for easier manipulation
         const pickerFields = Array.from(
            this.querySelectorAll("[data-picker-field]")
         );
         // Map each picker field to its corresponding value based on its type
         this.options = pickerFields
            .map((field) => {
               const type = field.dataset.pickerField;

               // For radio button fields, find the checked radio and return its value
               if (type === "radio") {
                  const checkedRadio = field.querySelector("input:checked");
                  return checkedRadio ? checkedRadio.value : undefined;
               }

               // For select fields, return the selected option's value
               if (false && field.querySelector("select")) {
                  return field.querySelector("select").value;
               }

               // Return undefined if no value is found (keeps the array's structure consistent)
               return undefined;
            })
            .filter((option) => option !== undefined); // Filter out undefined values to clean up the result
      }

      sqUpdateVariantPickerOptions() {
         let selectedDevice;
         // Find device option
         this.domNodes.pickerFields.forEach((field) => {
            const selectedValue = field.dataset.selectedValue;
            if (field.dataset.optionName === "Device") {
               selectedDevice = selectedValue;
            }
         });
         // Filter variants
         let availableVariants = [];
         if (selectedDevice) {
            for (let variant of this.getVisibleVariants()) {
               if (variant.options.length > 2) {
                  const variantDeviceOption = variant.option1;
                  if (variantDeviceOption === selectedDevice) {
                     availableVariants.push(variant);
                  }
               } else {
                  availableVariants.push(variant);
               }
            }
         }
         // Get preview images
         this.domNodes.pickerFields.forEach((field, index) => {
            if (field.dataset.optionName === "Color") {
               const nodes = field.querySelectorAll(".m-product-option--node");
               nodes.forEach((node) => {
                  for (let variant of availableVariants) {
                     if (
                        (variant.options.length > 2 &&
                           variant.option2 === node.dataset.value) ||
                        (variant.options.length <= 2 &&
                           variant.option1 === node.dataset.value)
                     ) {
                        // SQ: guard against null featured_image
                        if (!variant.featured_image || !variant.featured_image.src) continue;
                        const imageUrl = variant.featured_image.src;
                        const decodedUrl = imageUrl
                           .replaceAll(/&quot;/g, "'")
                           .replaceAll(/&amp;/g, "&");
                        const baseUrl = decodedUrl.split("?")[0];
                        const transformedUrl = `${baseUrl}?width=110&height=150&crop=center`;
                        // SQ: guard against missing label element
                        const label = node.querySelector("label[data-option-type='image']");
                        if (label) {
                           label.style.backgroundImage = `url(${transformedUrl})`;
                           label.classList.remove("option-loading");
                           break;
                        }
                     }
                  }
               });
            }
         });
      }

      updateSelectedOptions() {
         let selectedDevice;
         // Choose variants that are only in current device filter
         this.domNodes.pickerFields.forEach((field, index) => {
            const selectedValue = field.dataset.selectedValue;
            const optionValue = this.options[index];
            if (field.dataset.optionName === "Device") {
               selectedDevice = selectedValue;
            }
         });
         let availableVariants = [];
         if (selectedDevice) {
            for (let variant of this.getVisibleVariants()) {
               if (variant.options.length > 2) {
                  const variantDeviceOption = variant.option1;
                  if (variantDeviceOption === selectedDevice) {
                     availableVariants.push(variant);
                  }
               } else {
                  availableVariants.push(variant);
               }
            }
         }
         // Filter preview images
         this.domNodes.pickerFields.forEach((field, index) => {
            const selectedValue = field.dataset.selectedValue;
            const optionValue = this.options[index];

            if (field.dataset.optionName === "Color") {
               const nodes = field.querySelectorAll(".m-product-option--node");
               nodes.forEach((node) => {
                  for (let variant of availableVariants) {
                     if (variant.options.length > 2) {
                        const variantColorOption = variant.option2;
                        if (variantColorOption === node.dataset.value) {
                           // SQ: guard against null featured_image (Samsung products)
                           const label = node.querySelector("label[data-option-type='image']");
                           if (label && variant.featured_image && variant.featured_image.src) {
                              label.style.backgroundImage = `url(${variant.featured_image.src})`;
                           }
                        }
                     } else {
                        const variantColorOption = variant.option1;
                        if (variantColorOption === node.dataset.value) {
                           // SQ: guard against null featured_image (Samsung products)
                           const label = node.querySelector("label[data-option-type='image']");
                           if (label && variant.featured_image && variant.featured_image.src) {
                              label.style.backgroundImage = `url(${variant.featured_image.src})`;
                           }
                        }
                     }
                  }
               });
            }
            if (selectedValue !== optionValue) {
               const selectedOption = field.querySelector(
                  `input[value="${optionValue.replace(/["\\]/g, "\\$&")}"]`
               );
               if (selectedOption) {
                  selectedOption.checked = true;
                  field.updateSelectedValue();
               }
            }
         });
      }

      removeLoading(mediaElement) {
         if (!mediaElement) return;

         mediaElement.removeAttribute("data-media-loading");
         const firstChild = mediaElement.firstElementChild;
         if (firstChild) firstChild.style.opacity = 1;
      }

      handleImageZoom(mediaItems, mediaContext) {
         const customData = mediaItems.map((item) => {
            const { mediaType, index } = item.dataset;
            if (mediaType === "image") {
               const { mediaSrc, mediaWidth, mediaHeight, mediaAlt } =
                  item.querySelector(".m-product-media").dataset;
               return {
                  src: mediaSrc,
                  width: parseInt(mediaWidth),
                  height: parseInt(mediaHeight),
                  alt: mediaAlt,
                  id: index,
               };
            }
            return {
               html: `<div class="pswp__${mediaType}">${item.innerHTML}</div>`,
               type: mediaType,
               id: index,
            };
         });

         if (mediaContext && mediaContext.lightbox) {
            mediaContext.lightbox.destroy();
            mediaContext.handlePhotoswipe(customData);
            mediaContext.initPhotoswipe();
         }
      }

      updateMedia() {
         if (!this.currentVariant) return;

         // Define main and navigation items arrays
         let mainItems = [],
            navItems = [];

         const reorderItems = (
            referenceItems,
            items,
            isNav = false,
            isSlider = false
         ) => {
            let reorderedItems = [];

            referenceItems.forEach((referenceItem, index) => {
               const foundItem = items.find(
                  (item) =>
                     item.querySelector("[data-media-id]").dataset.mediaId ===
                     referenceItem
               );
               if (foundItem) {
                  foundItem.dataset.index = index;
                  foundItem.dataset.swiperSlideIndex = index;
                  reorderedItems.push(foundItem);
               }
            });

            items.forEach((item) => {
               const dataIdMedia =
                  item.querySelector("[data-media-id]").dataset.mediaId;
               if (
                  !referenceItems.includes(dataIdMedia) &&
                  !reorderedItems.includes(item)
               ) {
                  item.dataset.index = reorderedItems.length;
                  item.dataset.swiperSlideIndex = reorderedItems.length;
                  reorderedItems.push(item);
               }
            });

            if (isSlider) {
               reorderedItems.forEach((item) => {
                  if (isNav) {
                     item.classList.toggle(
                        "swiper-slide-thumb-active",
                        item.dataset.swiperSlideIndex === "0"
                     );
                  } else {
                     item.classList.toggle(
                        "swiper-slide-active",
                        item.dataset.swiperSlideIndex === "0"
                     );
                  }
               });
            }

            return reorderedItems;
         };

         // Common function to process slides
         const processItems = (currentItems, items) => {
            currentItems.forEach((slide) => {
               const dataIdMedia =
                  slide.querySelector("[data-media-id]").dataset.mediaId;

               const isCurrentVariantMedia =
                  this.currentVariantMedia &&
                  this.currentVariantMedia.length > 0 &&
                  this.currentVariantMedia.includes(dataIdMedia);
               const isNotImageType =
                  !slide.classList.contains("media-type-image");

               if (
                  isCurrentVariantMedia ||
                  isNotImageType ||
                  !this.currentVariantMedia ||
                  this.currentVariantMedia.length === 0
               ) {
                  items.push(slide);
               }
            });
         };

         // Process based on media mode
         if (this.variantGroupImages && this.variantGroupImages.enable) {
            if (this.media.mediaMode === "slider" && this.slides) {
               // Re-render main media
               processItems(this.slides, mainItems);
               const reorderedMainItems = reorderItems(
                  this.currentVariantMedia,
                  mainItems,
                  false,
                  true
               );
               // Update slider with new slides
               this.media.slider.removeAllSlides();
               this.media.slider.appendSlide(reorderedMainItems);
               this.media.slider.slideToLoop(
                  this.layout === "layout-7" ? 1 : 0
               );
               this.media.handleSlideChange();

               // Re-render nav media if available
               if (this.slidesNav) {
                  processItems(this.slidesNav, navItems);
                  const reorderedNavItems = reorderItems(
                     this.currentVariantMedia,
                     navItems,
                     true,
                     true
                  );
                  if (this.media.navSlider) {
                     this.media.navSlider.removeAllSlides();
                     this.media.navSlider.appendSlide(reorderedNavItems);
                     this.media.navSlider.slideToLoop(0);
                  }
               }
            } else {
               // Ensure mediaWrapper is available before proceeding.
               const mediaWrapper = this.media.querySelector(
                  ".m-product-media--list"
               );
               if (!mediaWrapper) return;

               // Use a more functional approach to filter and process items.
               processItems(this.mediaItems, mainItems);

               const reorderedItems = reorderItems(
                  this.currentVariantMedia,
                  mainItems
               );

               // Clear the mediaWrapper content once before appending new items.
               mediaWrapper.innerHTML = "";

               // Process and append the main items to the mediaWrapper.
               reorderedItems.forEach((item, index) => {
                  // Additional layout-specific adjustments can be made here.
                  if (this.layout === "layout-2") {
                     item.classList.remove("m-col-span-2");
                     if (index % 3 === 0) {
                        item.classList.add("m-col-span-2");
                     }
                  }
                  mediaWrapper.append(item); // Append each item to the mediaWrapper.
               });
            }
         } else {
            // Initialize mediaSize to 0 by default
            let mediaSize = 0;

            // Check if this.media and this.media.dataset.mediaSize exist before parsing
            if (this.media && "mediaSize" in this.media.dataset) {
               mediaSize = parseInt(this.media.dataset.mediaSize, 10);
            }

            // Proceed only if mediaSize is a positive number
            if (mediaSize > 0) {
               this.media.setActiveMedia(this.currentVariant);
            }
         }
         // Common operations after media update
         this.removeLoading(this.media);
         this.handleImageZoom(mainItems, this.media);
      }

      updateBrowserHistory() {
         if (this.currentVariant && this.dataset.updateUrl !== "false") {
            window.history.replaceState(
               {},
               "",
               `${this.productUrl}?variant=${this.currentVariant.id}`
            );
         }
      }

      updateVariantInput() {
         document
            .querySelectorAll(
               `#product-form-${this.sectionId}, #product-form-installment`
            )
            .forEach((productForm) => {
               const variantIdInput = productForm.querySelector(
                  this.selectors.variantIdInput
               );
               if (variantIdInput) {
                  variantIdInput.value = this.currentVariant.id;
                  variantIdInput.dispatchEvent(
                     new Event("input", { bubbles: true })
                  );
               }
            });
      }

      updatePickupAvailability() {
         const pickUpAvailability = this.productWrapper.querySelector(
            "pickup-availability"
         );

         if (!pickUpAvailability) return;

         if (!this.currentVariant || !this.currentVariant.available) {
            pickUpAvailability.removeAttribute("available");
            pickUpAvailability.innerHTML = "";
            return;
         }

         pickUpAvailability.fetchAvailability(this.currentVariant.id);
      }

      removeErrorMessage() {
         const section = this.closest("section");
         if (!section) return;

         const productForm = section.querySelector("product-form");
         if (productForm) {
            productForm.handleErrorMessage();
         }
      }

      updatePrice() {
         const classes = {
            onSale: "m-price--on-sale",
            soldOut: "m-price--sold-out",
         };

         const selectors = {
            priceWrapper: ".m-price",
            salePrice: ".m-price-item--sale",
            compareAtPrice: [".m-price-item--regular"],
            unitPrice: ".m-price__unit",
            unitPriceWrapper: ".m-price__unit-wrapper",
            saleBadge: ".m-price__badge-sale",
            saleAmount: "[data-saved-price]",
         };

         const mainProductPrices = this.productWrapper.querySelector(
            ".main-product__block-price"
         );
         if (!mainProductPrices) return;

         const moneyFormat = window.MinimogSettings.money_format;
         const {
            priceWrapper,
            salePrice,
            unitPrice,
            compareAtPrice,
            saleBadge,
            saleAmount,
            unitPriceWrapper,
         } = queryDomNodes(selectors, mainProductPrices);

         const { compare_at_price, price, unit_price_measurement, available } =
            this.currentVariant;
         const saleBadgeType = priceWrapper.dataset.saleBadgeType;

         const onSale = compare_at_price && compare_at_price > price;
         const soldOut = !available;

         priceWrapper.classList.toggle(classes.onSale, onSale);
         priceWrapper.classList.toggle(classes.soldOut, soldOut);
         priceWrapper.classList.remove("m:visibility-hidden");

         if (salePrice) {
            salePrice.innerHTML = formatMoney(price, moneyFormat);
         }

         if (compareAtPrice) {
            compareAtPrice.forEach(function (item) {
               const displayPrice = onSale ? compare_at_price : price;
               item.innerHTML = formatMoney(displayPrice, moneyFormat);
            });
         }

         if (saleBadge && onSale && saleBadgeType !== "text") {
            let value;
            if (saleBadgeType === "fixed_amount") {
               value = formatMoney(compare_at_price - price, moneyFormat);
            } else {
               const saving =
                  Math.round(
                     ((compare_at_price - price) * 100) / compare_at_price
                  ) + "%";
               value = saving;
            }
            if (saleAmount) {
               saleAmount.innerHTML = value;
            }
         }

         if (unit_price_measurement && unitPrice) {
            unitPriceWrapper.classList.remove("m:hidden");
            const unitPriceContent =
               "<span>" +
               formatMoney(this.currentVariant.unit_price, moneyFormat) +
               "</span>/<span data-unit-price-base-unit>" +
               this._getBaseUnit() +
               "</span>";
            unitPrice.innerHTML = unitPriceContent;
         } else if (unitPriceWrapper) {
            unitPriceWrapper.classList.add("m:hidden");
         }
      }

      _getBaseUnit() {
         const { reference_value, reference_unit } =
            this.currentVariant.unit_price_measurement;
         return reference_value === 1
            ? reference_unit
            : `${reference_value}${reference_unit}`;
      }

      updateButton(disable = true, text) {
         const productForms = document.querySelectorAll(
            `.product-form-${this.sectionId}`
         );
         if (!productForms.length) return;

         productForms.forEach((productForm) => {
            const addButton = productForm.querySelector('[name="add"]');
            if (!addButton) return;

            const dynamicCheckout = productForm.querySelector(
               ".m-product-dynamic-checkout"
            );
            const addButtonText = addButton.querySelector(
               ".m-add-to-cart--text"
            );

            const updateButtonState = (isDisabled) => {
               addButton.disabled = isDisabled;
               addButton.classList.toggle("disabled", isDisabled);
               if (dynamicCheckout) {
                  dynamicCheckout.classList.toggle("disabled", isDisabled);
               }
               addButtonText.textContent = text ? text : window.MinimogStrings.addToCart;
            };

            updateButtonState(disable);
         });
      }

updateVariantPickerVisibility() {
  const variantMetafield = this.variantMetafieldData.find(
    (metafield) => metafield.id === `${this.currentVariant.id}`
  );

  const allowedColorsRaw = variantMetafield?.metafields?.allowed_colors || "";
  if (!allowedColorsRaw) return;

  // 1) 현재 상품에서 "Color" 옵션의 position 찾기 (1,2,3...)
  let colorPosition = null;
  for (const optNode of this.domNodes.optionNodes) {
    const inputRadio = optNode.querySelector("input[type='radio']");
    if (inputRadio && inputRadio.name === "Color") {
      colorPosition = Number(optNode.dataset.optionPosition);
      break;
    }
  }
  if (!colorPosition) return;

  // 2) Visible variants 기준으로 "실제로 존재하는 Color 값" set 만들기
  const visibleVariants = this.getVisibleVariants();
  const visibleColorSet = new Set(
    visibleVariants
      .map((v) => (v.options && v.options[colorPosition - 1] ? v.options[colorPosition - 1] : null))
      .filter(Boolean)
  );

  // 3) allowed_colors 파싱
  let finalColors = [];

  // Device:Color 매핑 형태 ("Device:Black,Blue;Device2:Red,...") 인 경우
  if (allowedColorsRaw.includes(":")) {
    // 현재 선택된 Device 찾기
    let currentDevice = null;
    for (const optNode of this.domNodes.optionNodes) {
      const inputRadio = optNode.querySelector("input[type='radio']");
      if (inputRadio && inputRadio.name === "Device" && inputRadio.checked) {
        currentDevice = inputRadio.value;
        break;
      }
    }

    // Device 선택이 없으면(혹은 Device 옵션이 없으면) → 전체 visible 컬러만 허용
    if (!currentDevice) {
      finalColors = Array.from(visibleColorSet);
    } else {
      const allowedColorsList = allowedColorsRaw.split(";");
      for (const item of allowedColorsList) {
        if (!item) continue;
        const [device, colors] = item.split(":");
        if ((device || "").trim() === String(currentDevice).trim()) {
          finalColors = (colors || "")
            .split(",")
            .map((c) => c.trim())
            .filter(Boolean);
          break;
        }
      }
      // ✅ 중요: allowed_colors에 들어있어도 hidden variant 전용 컬러면 제거
      finalColors = finalColors.filter((c) => visibleColorSet.has(c));
    }
  } else {
    // 단순 리스트 형태 ("Black,Blue,Red") 인 경우
    const allowedList = allowedColorsRaw
      .split(",")
      .map((c) => c.trim())
      .filter(Boolean);

    // ✅ 중요: visible variants에 실제 존재하는 컬러만 남김
    finalColors = allowedList.filter((c) => visibleColorSet.has(c));
  }

  const finalColorSet = new Set(finalColors);

  // 4) Color 노드 show/hide 적용 (여기서 "다시 보여지는" 문제를 막음)
  for (const optNode of this.domNodes.optionNodes) {
    const inputRadio = optNode.querySelector("input[type='radio']");
    if (!inputRadio) continue;

    if (inputRadio.name === "Color") {
      const color = inputRadio.value;
      // finalColorSet에 없으면 숨김
      optNode.style.display = finalColorSet.has(color) ? "" : "none";
    }
  }
}

      updateProductMeta() {
         const { available, sku, barcode } = this.currentVariant;
         const { inStock, outOfStock } = window.MinimogStrings;
         const productAvailability = this.productWrapper.querySelector(
            this.selectors.productAvailability
         );
         const productSku = this.productWrapper.querySelector(
            this.selectors.productSku
         );
         const productBarcode = this.productWrapper.querySelector(
            this.selectors.productBarcode
         );
         const productBanner = this.productWrapper.querySelector(
            this.selectors.productBanner
         );
         const productCustomLabel = this.productWrapper.querySelector(
            this.selectors.productCustomLabel
         );
         const productSubtitle = this.productWrapper.querySelector(
            this.selectors.productSubtitle
         );
         const productInventoryStatus = this.productWrapper.querySelector(
            this.selectors.productInventoryStatus
         );
         const variantMetafield = this.variantMetafieldData.find(
            (metafield) => metafield.id === `${this.currentVariant.id}`
         );
         const invStatus = variantMetafield.metafields.inventory_status

         if (productCustomLabel) {
            if (variantMetafield?.metafields?.custom_label) {
               productCustomLabel.textContent =
                  variantMetafield?.metafields?.custom_label;
               productCustomLabel.style.display = "block";
            } else {
               productCustomLabel.textContent = "";
               productCustomLabel.style.display = "none";
            }
         }
         if (productSubtitle) {
            if (variantMetafield?.metafields?.subtitle) {
               productSubtitle.textContent =
                  variantMetafield?.metafields?.subtitle;
               productSubtitle.style.display = "block";
            } else {
               productSubtitle.textContent = "";
               productSubtitle.style.display = "none";
            }
         }

         if (productSku) {
            productSku.textContent = sku || "N/A";
         }
         if (productBarcode) {
            productBarcode.textContent = barcode || "N/A";
         }

         if (invStatus.toLowerCase().includes("preorder")) {
            this.ctaBtnText = "Preorder"
         } else if (invStatus.toLowerCase().includes("coming soon")) {
            this.ctaBtnText = "Coming soon"
         } else if (invStatus.toLowerCase().includes("backorder")) {
            this.ctaBtnText = "Backorder"
         } else if (!available) {
            this.ctaBtnText = window.MinimogStrings.soldOut
         } else {
            this.ctaBtnText = window.MinimogStrings.addToCart
         }

         if (
            productInventoryStatus &&
            variantMetafield?.metafields?.inventory_status &&
            variantMetafield.metafields.inventory_status !== "SOLD OUT" &&
            variantMetafield.metafields.inventory_status !== "COMING SOON"
         ) {
            productInventoryStatus.textContent =
               variantMetafield.metafields.inventory_status.split("- ")[1];
            productInventoryStatus.style.display = "block";
         } else {
            productInventoryStatus.style.display = "none";
         }

         if (productBanner) {
            // Clear existing banner content
            productBanner.innerHTML = "";
            // Check if we have variant metafield data with a banner
            if (variantMetafield?.metafields?.variant_banner) {
               const bannerValues = variantMetafield.metafields.variant_banner
                  .split(",")
                  .map((v) => v.trim().toLowerCase());
               const bannerConfig = {
                  google: {
                     src: "https://cdn.shopify.com/s/files/1/0808/0067/files/variant_banner_google.png?v=1738869032",
                     width: "140",
                     alt: "Google",
                  },
                  samsung: {
                     src: "https://cdn.shopify.com/s/files/1/0808/0067/files/variant_banner_samsung.png?v=1738869032",
                     width: "140",
                     alt: "Samsung",
                  },
                  qi2: {
                     src: "https://cdn.shopify.com/s/files/1/0808/0067/files/variant_banner_qi2.png?v=1738869033",
                     width: "65",
                     alt: "qi2",
                  },
               };
               bannerValues.forEach((value) => {
                  if (bannerConfig[value]) {
                     const newBanner = document.createElement("img");
                     const config = bannerConfig[value];
                     newBanner.src = config.src;
                     newBanner.width = config.width;
                     newBanner.alt = config.alt;
                     newBanner.style.height = "auto";
                     productBanner.appendChild(newBanner);
                  }
               });
            }
         }

         if (productAvailability) {
            productAvailability.textContent = available ? inStock : outOfStock;
            productAvailability.classList.toggle(
               "m-product-availability--outofstock",
               !available
            );
         }

         // Emit a custom event for product meta update
         const metaUpdateEvent = new CustomEvent("product:meta-updated", {
            detail: { available, sku },
         });
         this.productWrapper.dispatchEvent(metaUpdateEvent);
      }

      setUnavailable() {
         const button = document.getElementById(
            `product-form-${this.sectionId}`
         );
         if (!button) return;

         const addButton = button.querySelector('[name="add"]');
         if (!addButton) return;

         const addButtonText = addButton.querySelector(".m-add-to-cart--text");
         if (addButtonText) {
            addButtonText.textContent = window.MinimogStrings.unavailable;
         }

         const priceWrapper = this.productWrapper.querySelector(".m-price");
         if (priceWrapper) {
            priceWrapper.classList.add("m:visibility-hidden");
         }
      }

      hideSoldOutAndUnavailableOptions() {
         const classes = {
            soldOut: "m-product-option--node__soldout",
            unavailable: "m-product-option--node__unavailable",
         };
         const { optionNodes } = this.domNodes;
         const { options } = this.productData;
         const variants = this.getVisibleVariants();

         optionNodes.forEach((optNode) => {
            const { optionPosition, value } = optNode.dataset;
            const optPos = Number(optionPosition);
            const isSelectOption = optNode.tagName === "OPTION";

            let matchVariants = [];
            if (optPos === options.length) {
               const optionsArray = [...this.currentVariant.options];
               optionsArray[optPos - 1] = value;
               matchVariants.push(
                  this.sqGetVariantFromOptionArray(
                     this.productData,
                     optionsArray,
                     this.variantMetafieldData
                  )
               );
            } else {
               matchVariants = variants.filter(
                  (v) =>
                     v.options[optPos - 1] === value &&
                     v.options[optPos - 2] ===
                        this.currentVariant[`option${optPos - 1}`]
               );
            }

            matchVariants = matchVariants.filter(Boolean);
            const isSoldOut =
               matchVariants.length && matchVariants.every((v) => !v.available);
            const isUnavailable = !matchVariants.length;

// Hide values that only lead to hidden variants
if (isSelectOption) {
   optNode.disabled = isUnavailable;
   optNode.hidden = isUnavailable;
} else {
   // For button/image/color nodes
   optNode.style.display = isUnavailable ? "none" : "";
}

optNode.classList.toggle(classes.soldOut, isSoldOut);
            optNode.classList.toggle(classes.unavailable, isUnavailable);
         });
      }


/**
 * Variant visibility helper (custom.variant_visibility).
 * Treat missing metafield as visible.
 */
isVariantVisible(variantId) {
   const mf = (this.variantMetafieldData || []).find(
      (m) => String(m.id) === String(variantId)
   );
   const v = mf && mf.metafields ? mf.metafields.variant_visibility : undefined;

   if (v === undefined || v === null) return true;
   if (typeof v === "string") return v.toLowerCase() !== "false";
   return v !== false;
}

getVisibleVariants() {
   const variants = (this.productData && this.productData.variants) || [];
   return variants.filter((v) => this.isVariantVisible(v.id));
}

      getVariantData() {
         if (this.variantData) {
            return this.variantData;
         }
         const productVariants = this.querySelector(
            '#productVariants[type="application/json"]'
         );
         if (productVariants) {
            try {
               this.variantData = JSON.parse(productVariants.textContent);
            } catch (error) {
               console.error("Error parsing variant data:", error);
            }
         }
         return this.variantData || {};
      }

      // SQ: load product images JSON (used for SKU-based swatch fallback)
      getProductImages() {
         if (this._productImages) return this._productImages;
         const el = this.querySelector('#productImages[type="application/json"]');
         if (el) {
            try { this._productImages = JSON.parse(el.textContent || "[]"); } catch (e) {}
         }
         return this._productImages || [];
      }
      getVariantMetafieldData() {
         if (this.variantMetafieldData) {
            return this.variantMetafieldData;
         }

         // Try both the generic name selector and ID-based selector
         const variantMetafieldElement = this.querySelector(
            'script[type="application/json"][name="sq_variant_metafields"]'
         );
         if (variantMetafieldElement) {
            try {
               this.variantMetafieldData = JSON.parse(
                  variantMetafieldElement.textContent
               );
            } catch (error) {
               console.error("Error parsing variant metafield data:", error);
               this.variantMetafieldData = [];
            }
         } else {
            console.warn("Variant metafield element not found");
            this.variantMetafieldData = [];
         }

         return this.variantMetafieldData;
      }

      getVariantGroupImageData() {
         if (this.variantGroupImages) {
            return this.variantGroupImages;
         }
         const variantGroupElement = this.querySelector(
            '#variantGroup[type="application/json"]'
         );
         if (variantGroupElement) {
            try {
               this.variantGroupImages = JSON.parse(
                  variantGroupElement.textContent
               );
            } catch (error) {
               console.error("Error parsing variant group images data:", error);
            }
         }
         return this.variantGroupImages || {};
      }

      initOptionSwatches() {
         const { _colorSwatches = [], _imageSwatches = [] } =
            window.MinimogSettings;

         if (!this.domNodes.optionNodes) return;

         // Get selected device to filter variants
         let selectedDevice;
         this.domNodes.pickerFields.forEach((field) => {
            const selectedValue = field.dataset.selectedValue;
            if (field.dataset.optionName === "Device") {
               selectedDevice = selectedValue;
            }
         });

         // Filter variants based on selected device
         let availableVariants = [];
         if (selectedDevice) {
            for (let variant of this.getVisibleVariants()) {
               if (variant.options.length > 2) {
                  const variantDeviceOption = variant.option1;
                  if (variantDeviceOption === selectedDevice) {
                     availableVariants.push(variant);
                  }
               } else {
                  availableVariants.push(variant);
               }
            }
         } else {
            availableVariants = this.getVisibleVariants();
         }

         this.domNodes.optionNodes.forEach((optNode) => {
            const {
               optionType,
               optionPosition,
               value: optionValue,
            } = optNode.dataset;
            const optionValueLowerCase =
               optionValue && optionValue.toLowerCase();

            // Find appropriate variant for swatch image
            let variantToShowSwatchImage;

            if (
               optNode.closest("[data-option-name]")?.dataset.optionName ===
               "Color"
            ) {
               // For color options, find a variant from filtered variants
               // Use optionPosition to correctly identify which option slot holds Color
               variantToShowSwatchImage = availableVariants.find((variant) => {
                  return variant[`option${optionPosition}`] === optionValue;
               });
            } else {
               // For other options, use original logic
               variantToShowSwatchImage = this.variantData.find(
                  (v) => v[`option${optionPosition}`] === optionValue
               );
            }

            const variantImage =
               variantToShowSwatchImage &&
               variantToShowSwatchImage.featured_image &&
               variantToShowSwatchImage.featured_image.src
                  ? getSizedImageUrl(
                       variantToShowSwatchImage.featured_image.src,
                       "100x"
                    )
                  : null;

            // SQ: use data-swatch-img (from Liquid slug match) when no featured_image
            let resolvedVarImage = variantImage;
            if (!resolvedVarImage) {
               const swatchImgUrl = optNode.dataset.swatchImg;
               if (swatchImgUrl) resolvedVarImage = swatchImgUrl;
            }

            const customImage = _imageSwatches.find(
               (i) => i.key === optionValueLowerCase
            );
            const customColor = _colorSwatches.find(
               (c) => c.key === optionValueLowerCase
            );

            const labelElement = optNode.querySelector("label");
            if (!labelElement) return;

            if (resolvedVarImage || (customImage && customImage.value)) {
               labelElement.classList.add("has-bg-img");
            }

            labelElement.classList.remove("option-loading");

            switch (optionType) {
               case "default":
               case "image":
                  // Decode any HTML entities in the URL before setting it
                  const imageUrl =
                     resolvedVarImage || (customImage && customImage.value) || "";
                  if (imageUrl) {
                     const decodedUrl = imageUrl
                        .replaceAll(/&quot;/g, "'")
                        .replaceAll(/&amp;/g, "&");
                     // Extract the base URL without any existing parameters
                     const baseUrl = decodedUrl.split("?")[0];
                     // Apply Shopify's image transformation
                     const transformedUrl = `${baseUrl}?width=110&height=150&crop=center`;
                     labelElement.style.backgroundImage = `url(${transformedUrl})`;
                     labelElement.style.backgroundSize = "cover";
                     labelElement.style.backgroundPosition = "center center";
                  } else if (customColor && customColor.value) {
                     // SQ: fallback to hex color when no variant image is available
                     labelElement.style.backgroundColor = customColor.value;
                  }
                  break;
               case "color":
                  labelElement.style.backgroundColor =
                     (customColor && customColor.value) || optionValueLowerCase;
                  if (customImage && customImage.value) {
                     labelElement.style.backgroundImage = `url(${decodedCustomUrl})`;
                  }
                  break;
               default:
                  break;
            }
         });
      }
   }

   customElements.define("variant-picker", VariantPicker);
}
if (!customElements.get("variant-button")) {
   class VariantButton extends HTMLElement {
      constructor() {
         super();
         this.selectedSpan = this.querySelector(".option-label--selected");
         this.addEventListener("change", this.updateSelectedValue);

         // Get the option position to identify related components
         this.optionName = this.dataset.optionName;

         // Find related variant-select with same option position
         this.relatedSelect = this.closest("variant-picker").querySelector(
            `variant-select[data-option-name="${this.optionName}"]`
         );
      }

      updateSelectedValue() {
         const checkedInput = Array.from(this.querySelectorAll("input")).find(
            (radio) => radio.checked
         );
         if (!checkedInput) return;

         this.value = checkedInput.value;
         this.setAttribute("data-selected-value", this.value);

         if (this.selectedSpan) {
            this.selectedSpan.textContent = this.value;
            this.selectedSpan.classList.remove("m:text-color-error");
         }

         // Update related select if it exists
         if (this.relatedSelect) {
            this.relatedSelect.dataset.selectedValue = this.value;
            const select = this.relatedSelect.querySelector("select");
            const options = select.options;
            for (let i = 0; i < options.length; i++) {
               if (options[i].value === this.value) {
                  options[i].selected = true;
                  break;
               } else {
                  options[i].selected = false;
               }
            }
            this.relatedSelect.updateSelectedValue();
         }
      }
   }

   customElements.define("variant-button", VariantButton);

   if (!customElements.get("variant-select")) {
      class VariantSelect extends VariantButton {
         constructor() {
            super();
            this.optionName = this.dataset.optionName;
            // Find related variant-button with same option position
            this.relatedButton = this.closest("variant-picker").querySelector(
               `variant-button[data-option-name="${this.optionName}"]`
            );
         }

         updateSelectedValue() {
            const select = this.querySelector("select");
            if (!select) return;

            this.value = select.value;
            this.setAttribute("data-selected-value", this.value);

            if (this.selectedSpan) {
               this.selectedSpan.textContent = this.value;
               this.selectedSpan.classList.remove("m:text-color-error");
            }

            // Update related radio button if it exists
            if (this.relatedButton) {
               this.relatedButton.dataset.selectedValue = this.value;
               const nodes = this.relatedButton.querySelectorAll(
                  ".m-product-option--node"
               );
               for (const node of nodes) {
                  const input = node.querySelector("input");
                  if (input && input.value === this.value) {
                     input.checked = true;
                     break;
                  } else {
                     input.checked = false;
                  }
               }
            }
         }
      }

      customElements.define("variant-select", VariantSelect);
   }
   if (!customElements.get("variant-image")) {
      class VariantImage extends VariantButton {
         constructor() {
            super();
         }
      }

      customElements.define("variant-image", VariantImage);
   }
   if (!customElements.get("variant-color")) {
      class VariantColor extends VariantButton {
         constructor() {
            super();
         }
      }
      customElements.define("variant-color", VariantColor);
   }
}
